import React from 'react';
import { Grid, TextField, Autocomplete, Typography, Box, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, ListSubheader, FormHelperText } from '@mui/material';
import { anaesthesiaOptions, doctorGroups } from '../constants/options';

const CarePlanSection = ({ formData, handleChange, handleNameChange, handleBlur, setFormData, errors }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ color: '#008080', mb: 2, fontWeight: 'bold' }}>
        Acknowledgement & Care Plan
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ lineHeight: 1.8, textAlign: 'justify', mb: 2 }}>
            I, <TextField
                  variant="standard"
                  name="acknowledge_patient_name"
                  value={formData.acknowledge_patient_name || ''}
                  onChange={handleNameChange}
                  sx={{ width: '200px', mx: 1 }}
                />
            acknowledge that my doctor has explained to me that I will have an operation, diagnostic or treatment procedure. My doctor has explained the cost, risks of the procedure, advised me of the alternative treatments and the risks of not having the procedure and told me about the expected outcome and what could happen if my condition remains untreated. I also understand that anaesthesia services are needed so that my doctor can perform the operation or procedure. It has been explained to me that all forms of anaesthesia involve some and no guarantees or promises can be made concerning the results of my procedure or treatment. Although rare, unexpected severe complications with anaesthesia can occur and include the remote possibility of infection, bleeding, drug reactions, blood clots, loss of sensation, loss of limb function, paralysis, stroke, brain damage, heart attack or death. I understand that these risks apply to all forms of anaesthesia and that additional or specific risks have been identified below as they may apply to a specific type of anaesthesia. I understand that the type(s) of anaesthesia service checked below will be used for my procedure and that the anaesthesia technique to be used is determined by many factors including my physical condition, the type of procedure my doctor is to do, his or her preference, as well as my own desire. It has been explained to me that sometimes an anaesthesia technique which involves the use of local anaesthetics, with or without sedation, may not succeed completely and therefore another technique may have to be used including general anaesthesia.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            options={anaesthesiaOptions}
            value={formData.anaesthesia_care_plan || ''}
            onChange={(e, val) => setFormData(prev => ({ ...prev, anaesthesia_care_plan: val }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<span>Anaesthesia Care Plan <span style={{ color: 'red' }}>*</span></span>}
                name="anaesthesia_care_plan"
                onBlur={handleBlur}
                error={!!errors.anaesthesia_care_plan}
                helperText={errors.anaesthesia_care_plan}
                onChange={handleChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Types of Anaesthesia:
          </Typography>
          <Grid container spacing={3}>
            {/* General Anaesthesia */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <FormControlLabel
                  control={<Checkbox name="general_anaesthesia" checked={formData.general_anaesthesia} onChange={handleChange} />}
                  label={<Typography sx={{ fontWeight: 'bold' }}>General Anaesthesia</Typography>}
                />
                <Box sx={{ ml: 4, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <b>Expected Result:</b> Total unconscious state, possible placement of a tube into the windpipe.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Techniques:</b> Drug injected into the bloodstream, breathed into the lungs, or by other routes.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Risks:</b> Mouth/throat pain, hoarseness, injury to mouth/teeth, awareness under anaesthesia, injury to blood vessels, aspiration, pneumonia.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Spinal or Epidural */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Spinal or Epidural Analgesia / Anaesthesia</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="spinal_epidural_with_sedation" checked={formData.spinal_epidural_with_sedation} onChange={handleChange} />}
                      label="With Sedation"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="spinal_epidural_without_sedation" checked={formData.spinal_epidural_without_sedation} onChange={handleChange} />}
                      label="Without Sedation"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ ml: 4, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <b>Expected Result:</b> Temporary decreased or loss of feeling and/ or movement to lower part of the body.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Techniques:</b> Drug injected through a needle / catheter placed either directly into the spinal canal or immediately outside the spinal canal.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Risks:</b> Headache, backache, buzzing in the ears, convulsions, infection, persistent weakness, residual pain, injury to blood vessels, "total spinal".
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Major / Minor Nerve Block */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Major / Minor Nerve Block</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="major_minor_nerve_with_sedation" checked={formData.major_minor_nerve_with_sedation} onChange={handleChange} />}
                      label="With Sedation"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="major_minor_nerve_without_sedation" checked={formData.major_minor_nerve_without_sedation} onChange={handleChange} />}
                      label="Without Sedation"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ ml: 4, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <b>Expected Result:</b> Temporary loss of feeling and/or movement of a specific limb or area.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Techniques:</b> Drug injected near to the nerves providing loss of sensation to the area of the operation.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Risks:</b> Infection, convulsions, persistent numbness, residual pain, injury to blood vessels.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 3 }}>
          <Typography component="div" sx={{ lineHeight: 1.9, mb: 2 }}>
            I hereby consent to the anaesthesia service checked above and authorize that it be administered by&nbsp;
            <FormControl
              size="small"
              variant="outlined"
              error={!!errors.dr_name}
              sx={{ minWidth: 220, display: 'inline-flex', verticalAlign: 'middle', mx: 1 }}
            >
              <InputLabel id="dr-name-label">Doctor <span style={{ color: 'red' }}>*</span></InputLabel>
              <Select
                labelId="dr-name-label"
                name="dr_name"
                value={formData.dr_name || ''}
                onChange={handleChange}
                label={<span>Doctor <span style={{ color: 'red' }}>*</span></span>}
                onBlur={handleBlur}
              >
                <MenuItem value=""><em>Select Doctor</em></MenuItem>
                {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                  <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                  ...doctors.map((doctor, idx) => (
                    <MenuItem key={`${specialty}-${idx}`} value={doctor}>{doctor}</MenuItem>
                  )),
                ])}
              </Select>
              {errors.dr_name && <FormHelperText>{errors.dr_name}</FormHelperText>}
            </FormControl>
            &nbsp;or&nbsp;
            <FormControl
              size="small"
              variant="outlined"
              sx={{ minWidth: 140, display: 'inline-flex', verticalAlign: 'middle', mx: 1 }}
            >
              <InputLabel id="dr-his-her-label">His / Her</InputLabel>
              <Select
                labelId="dr-his-her-label"
                name="dr_his_her"
                value={formData.dr_his_her || ''}
                onChange={handleChange}
                label="His / Her"
              >
                <MenuItem value="his">His</MenuItem>
                <MenuItem value="her">Her</MenuItem>
                <MenuItem value="their">Their</MenuItem>
              </Select>
            </FormControl>
            &nbsp;associates; all of whom are credentialed to provide anaesthesia service at Annai Arul Hospitals, Mudichur - Chennai. I also consent to an alternative type of anaesthesia, if necessary, as deemed appropriate by them.
          </Typography>
          <Typography variant="body2">
            I certify and acknowledge that I have read this form or had it read to me, that I understand the risks, alternatives, and expected results of the anaesthesia service and that I had ample time to ask questions and consider my decision.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CarePlanSection;
